import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getSocket } from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";
import TaskDetail from "./TaskDetail";
import TemplatesManager from "./TemplatesManager";

// feat_296 — Checklist interno del equipo (Mariano + colaboradores).
// Solo superadmin, gateado por el permiso "checklist" de la matriz
// superadminPermissions. CROSS-COMPANY: el cliente asociado a una tarea es una
// etiqueta opcional, no un scope.
//
// Tiempo real: room dedicada `superadmin-checklist` (los superadmins tienen
// companyId NULL y no entran a las rooms company-*). Se une con el emit
// `joinSuperadminChecklist`; el backend verifica isSuperAdmin contra la DB.

const STATUS_LABELS = {
  pending: "Pendiente",
  in_progress: "En progreso",
  done: "Hecho",
};

const STATUS_COLORS = {
  pending: "#9e9e9e",
  in_progress: "#ff9800",
  done: "#4caf50",
};

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  controls: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
    margin: theme.spacing(2, 0),
  },
  paper: { overflowX: "auto" },
  progressCell: { minWidth: 180 },
  // feat_297 — barra propia en vez de <LinearProgress>. El determinate de
  // MUI v4 pinta el TRACK con `colorPrimary` = lighten(primary, .62), un azul
  // bien visible: a 0% se veía el track entero relleno y la barra real (width
  // 0) quedaba invisible contra él → parecía "casi llena con 0%".
  // Acá el track es gris neutro y el fill lleva width EXACTO = progressPct%.
  progressWrap: { display: "flex", alignItems: "center", gap: 8 },
  track: {
    flexGrow: 1,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#e0e0e0",
    overflow: "hidden",
  },
  fill: {
    height: "100%",
    borderRadius: 4,
    transition: "width .3s ease",
  },
  pct: { minWidth: 34, textAlign: "right", fontVariantNumeric: "tabular-nums" },
  detailCell: { padding: 0, borderBottom: "none" },
  detailWrap: { padding: theme.spacing(2), background: theme.palette.action.hover },
  spacer: { flexGrow: 1 },
}));

const StatusChip = ({ status }) => (
  <Chip
    size="small"
    label={STATUS_LABELS[status] || status}
    style={{ backgroundColor: STATUS_COLORS[status], color: "#fff" }}
  />
);

// feat_297 — verde a 100%, ámbar en progreso, gris (invisible sobre el track)
// en 0%. El valor viene SIEMPRE de task.progressPct persistido en la DB; el
// frontend no recalcula nada.
const fillColor = (pct) => {
  if (pct >= 100) return "#4caf50";
  if (pct > 0) return "#ff9800";
  return "transparent";
};

const ProgressBar = ({ pct, classes }) => {
  // Clamp defensivo: el ancho nunca puede salirse de 0..100.
  const safe = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div className={classes.progressWrap}>
      <div className={classes.track}>
        <div
          className={classes.fill}
          style={{ width: `${safe}%`, backgroundColor: fillColor(safe) }}
        />
      </div>
      <Typography variant="caption" className={classes.pct}>
        {safe}%
      </Typography>
    </div>
  );
};

const SuperAdminChecklist = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  // feat_297 — "master" = super superadmin (sin marca). Mismo criterio que el
  // backend (isSuperSuperadmin) y que el `canDelete` del Panel general.
  const isMaster = !!user?.isSuperAdmin && !user?.whitelabelScopeId;
  const myId = Number(user?.id);

  const [tab, setTab] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyTaskId, setBusyTaskId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const [filterCompany, setFilterCompany] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAssignee, setFilterAssignee] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    companyId: "",
    templateId: "",
    assignedTo: "",
  });
  const [creating, setCreating] = useState(false);

  // Los filtros vivos se leen desde el listener de socket sin re-suscribirlo.
  const filtersRef = useRef({ company: "", status: "", assignee: "" });
  useEffect(() => {
    filtersRef.current = {
      company: filterCompany,
      status: filterStatus,
      assignee: filterAssignee,
    };
  }, [filterCompany, filterStatus, filterAssignee]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCompany) params.companyId = filterCompany;
      if (filterStatus) params.status = filterStatus;
      // El filtro por responsable es solo del master: para un no-master el
      // backend ya acota a lo suyo e ignora este parámetro.
      if (isMaster && filterAssignee) params.assignedTo = filterAssignee;
      const { data } = await api.get("/superadmin/checklist/tasks", { params });
      setTasks(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [filterCompany, filterStatus, filterAssignee, isMaster]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    (async () => {
      try {
        const [c, t, a] = await Promise.all([
          api.get("/superadmin/checklist/companies"),
          api.get("/superadmin/checklist/templates"),
          api.get("/superadmin/checklist/assignees"),
        ]);
        setCompanies(c.data);
        setTemplates(t.data);
        setAssignees(a.data);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  // ── Tiempo real ──────────────────────────────────────────────────────────
  // Patrón obligatorio del proyecto (feat_200): getSocket() + emit inmediato +
  // re-emit en "connect" + cleanup con socket.off, NUNCA socket.disconnect()
  // (el socket es SINGLETON compartido con el resto de la app).
  useEffect(() => {
    const socket = getSocket();

    const join = () => socket.emit("joinSuperadminChecklist");
    join();

    const matchesFilters = (task) => {
      const { company, status, assignee } = filtersRef.current;
      // feat_297 — VISIBILIDAD primero. El evento va a toda la room
      // `superadmin-checklist`, así que un no-master también recibe tareas
      // ajenas: sin este corte le aparecerían en vivo aunque el GET no se las
      // devuelva. Espejo exacto del where del backend.
      if (!isMaster && task.createdBy !== myId && task.assignedTo !== myId) {
        return false;
      }
      if (status && task.status !== status) return false;
      if (company === "none" && task.companyId !== null) return false;
      if (company && company !== "none" && String(task.companyId) !== String(company)) {
        return false;
      }
      if (assignee === "none" && task.assignedTo !== null) return false;
      if (assignee && assignee !== "none" && String(task.assignedTo) !== String(assignee)) {
        return false;
      }
      return true;
    };

    const handleChecklist = (data) => {
      if (data?.action === "task-delete") {
        setTasks((prev) => prev.filter((t) => t.id !== data.taskId));
        setExpandedId((cur) => (cur === data.taskId ? null : cur));
        return;
      }
      const task = data?.task;
      if (!task) return;

      setTasks((prev) => {
        const exists = prev.some((t) => t.id === task.id);
        // Una tarea que dejó de matchear los filtros activos sale de la lista.
        if (!matchesFilters(task)) {
          return exists ? prev.filter((t) => t.id !== task.id) : prev;
        }
        return exists
          ? prev.map((t) => (t.id === task.id ? task : t))
          : [task, ...prev];
      });
    };

    socket.on("connect", join);
    socket.on("checklist", handleChecklist);

    return () => {
      socket.off("connect", join);
      socket.off("checklist", handleChecklist);
    };
    // isMaster/myId entran en el gate de visibilidad del handler: si el user
    // del contexto llega async, el efecto tiene que re-suscribir con el valor
    // bueno (si no, el primer render filtraría con myId = NaN).
  }, [isMaster, myId]);

  // Todas las mutaciones de tarea/item devuelven la tarea completa y fresca.
  const applyTask = (fresh) =>
    setTasks((prev) => prev.map((t) => (t.id === fresh.id ? fresh : t)));

  const withBusy = async (taskId, fn) => {
    setBusyTaskId(taskId);
    try {
      const { data } = await fn();
      applyTask(data);
    } catch (err) {
      toastError(err);
      // Estado desconocido tras el error → releer del servidor.
      fetchTasks();
    } finally {
      setBusyTaskId(null);
    }
  };

  const handleCreate = async () => {
    const title = form.title.trim();
    if (!title) return;
    setCreating(true);
    try {
      await api.post("/superadmin/checklist/tasks", {
        title,
        description: form.description.trim() || null,
        companyId: form.companyId || null,
        templateId: form.templateId || null,
        assignedTo: form.assignedTo || null,
      });
      setCreateOpen(false);
      setForm({
        title: "",
        description: "",
        companyId: "",
        templateId: "",
        assignedTo: "",
      });
      fetchTasks();
    } catch (err) {
      toastError(err);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("¿Eliminar la tarea? Queda registrado quién la borró.")) return;
    setBusyTaskId(taskId);
    try {
      await api.delete(`/superadmin/checklist/tasks/${taskId}`);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      if (expandedId === taskId) setExpandedId(null);
    } catch (err) {
      toastError(err);
    } finally {
      setBusyTaskId(null);
    }
  };

  const renderTaskRow = (task) => {
    const expanded = expandedId === task.id;
    return (
      <React.Fragment key={task.id}>
        <TableRow hover>
          <TableCell>
            <IconButton
              size="small"
              onClick={() => setExpandedId(expanded ? null : task.id)}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </TableCell>
          <TableCell>
            <Typography variant="body2">{task.title}</Typography>
            {task.description && (
              <Typography variant="caption" color="textSecondary">
                {task.description}
              </Typography>
            )}
          </TableCell>
          <TableCell>{task.company ? task.company.name : "—"}</TableCell>
          <TableCell className={classes.progressCell}>
            <ProgressBar pct={task.progressPct} classes={classes} />
            <Typography variant="caption" color="textSecondary">
              {(task.items || []).length} items
            </Typography>
          </TableCell>
          <TableCell>
            <StatusChip status={task.status} />
          </TableCell>
          <TableCell>
            {task.assignee ? (
              <Chip size="small" variant="outlined" label={task.assignee.name} />
            ) : (
              <Typography variant="caption" color="textSecondary">
                Sin asignar
              </Typography>
            )}
          </TableCell>
          <TableCell>
            <Typography variant="caption" color="textSecondary">
              {task.creator ? task.creator.name : "—"}
            </Typography>
          </TableCell>
          <TableCell align="right">
            <IconButton
              size="small"
              disabled={busyTaskId === task.id}
              onClick={() => handleDeleteTask(task.id)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={classes.detailCell} colSpan={8}>
            <Collapse in={expanded} unmountOnExit>
              <div className={classes.detailWrap}>
                <TaskDetail
                  task={task}
                  busy={busyTaskId === task.id}
                  onAddItem={(payload) =>
                    withBusy(task.id, () =>
                      api.post(`/superadmin/checklist/tasks/${task.id}/items`, payload)
                    )
                  }
                  onUpdateItem={(itemId, payload) =>
                    withBusy(task.id, () =>
                      api.put(`/superadmin/checklist/items/${itemId}`, payload)
                    )
                  }
                  onDeleteItem={(itemId) =>
                    withBusy(task.id, () =>
                      api.delete(`/superadmin/checklist/items/${itemId}`)
                    )
                  }
                  onReorder={(itemIds) =>
                    withBusy(task.id, () =>
                      api.put(
                        `/superadmin/checklist/tasks/${task.id}/items/reorder`,
                        { itemIds }
                      )
                    )
                  }
                  onToggleSelfDone={(selfDone) =>
                    withBusy(task.id, () =>
                      api.put(`/superadmin/checklist/tasks/${task.id}`, { selfDone })
                    )
                  }
                  onAssign={(assignedTo) =>
                    withBusy(task.id, () =>
                      api.put(`/superadmin/checklist/tasks/${task.id}`, {
                        assignedTo,
                      })
                    )
                  }
                  assignees={assignees}
                  isMaster={isMaster}
                />
              </div>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>
        Checklist
      </Typography>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="Tareas" />
        <Tab label="Plantillas" />
      </Tabs>

      {tab === 0 && (
        <>
          <div className={classes.controls}>
            <Select
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
              displayEmpty
              style={{ minWidth: 200 }}
            >
              <MenuItem value="">Todos los clientes</MenuItem>
              <MenuItem value="none">
                <em>Sin cliente asociado</em>
              </MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              displayEmpty
              style={{ minWidth: 160 }}
            >
              <MenuItem value="">Todos los estados</MenuItem>
              <MenuItem value="pending">Pendiente</MenuItem>
              <MenuItem value="in_progress">En progreso</MenuItem>
              <MenuItem value="done">Hecho</MenuItem>
            </Select>

            {/* feat_297 — filtro por responsable: SOLO el master. Un no-master
                ya ve exclusivamente lo suyo, así que el filtro no tendría
                nada que acotar (y el backend ignora el parámetro). */}
            {isMaster && (
              <Select
                value={filterAssignee}
                onChange={(e) => setFilterAssignee(e.target.value)}
                displayEmpty
                style={{ minWidth: 180 }}
              >
                <MenuItem value="">Todos los responsables</MenuItem>
                <MenuItem value="none">
                  <em>Sin asignar</em>
                </MenuItem>
                {assignees.map((a) => (
                  <MenuItem key={a.id} value={String(a.id)}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
            )}

            <Button size="small" variant="outlined" onClick={fetchTasks}>
              Actualizar
            </Button>

            <span className={classes.spacer} />

            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
            >
              Crear tarea
            </Button>
          </div>

          <Paper className={classes.paper}>
            {loading ? (
              <CircularProgress style={{ margin: 16 }} />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: 48 }} />
                    <TableCell>Tarea</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Progreso</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Asignada a</TableCell>
                    <TableCell>Creada por</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography variant="body2" color="textSecondary">
                          No hay tareas con estos filtros.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tasks.map(renderTaskRow)
                  )}
                </TableBody>
              </Table>
            )}
          </Paper>
        </>
      )}

      {tab === 1 && <TemplatesManager />}

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Crear tarea</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Título"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            margin="dense"
            label="Descripción (opcional)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <Select
            fullWidth
            displayEmpty
            style={{ marginTop: 16 }}
            value={form.companyId}
            onChange={(e) => setForm({ ...form, companyId: e.target.value })}
          >
            <MenuItem value="">
              <em>Sin cliente asociado</em>
            </MenuItem>
            {companies.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
          <Select
            fullWidth
            displayEmpty
            style={{ marginTop: 16 }}
            value={form.templateId}
            onChange={(e) => setForm({ ...form, templateId: e.target.value })}
          >
            <MenuItem value="">
              <em>Sin plantilla (tarea vacía)</em>
            </MenuItem>
            {templates.map((t) => (
              <MenuItem key={t.id} value={String(t.id)}>
                {t.name} ({(t.items || []).length} items)
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="textSecondary">
            La plantilla copia sus items vacíos; después los podés editar.
          </Typography>

          {/* feat_302 — el selector lo ve TODO el mundo. La lista viene ya
              recortada por el backend (GET /checklist/assignees): el master
              recibe a todos; un no-master recibe [él mismo + el/los owner],
              que es exactamente lo que resolveAssignedTo le va a aceptar. */}
          <Select
            fullWidth
            displayEmpty
            style={{ marginTop: 16 }}
            value={form.assignedTo}
            onChange={(e) => setForm({ ...form, assignedTo: e.target.value })}
          >
            <MenuItem value="">
              <em>Sin asignar</em>
            </MenuItem>
            {assignees.map((a) => (
              <MenuItem key={a.id} value={String(a.id)}>
                {a.name}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="textSecondary">
            {isMaster
              ? "Asignar a — solo aparecen quienes tienen el permiso Checklist."
              : "Asignar a — podés dejarla tuya o pasársela al owner."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={creating || !form.title.trim()}
            onClick={handleCreate}
          >
            Crear
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SuperAdminChecklist;
